import { get, isEqual, max } from "lodash";
import { AxiosInstance } from "axios";
import { logError } from "../utils/log";
import { getExtension } from "../utils/files";
import { when } from "../utils/functions";
import { DocumentType } from "../common/documents";

export type SupportedFileType = { extensions: string[]; magicBytes: number[][] };

export type FileUploadConfig = {
  supportedFileTypes: SupportedFileType[];
  maxFileSize: number;
  maxDimensions?: Dimensions;
};

export type Dimensions = { width: number; height: number };

export const emptyFileUploadConfig = (): FileUploadConfig => ({
  supportedFileTypes: [],
  maxFileSize: 0,
});

export type InvalidFileError = { type: "invalid-file"; filename: string; bytes: number[] };
export type UnsupportedFilesError = {
  type: "unsupported-files";
  filenames: string[];
  supportedFileTypes: SupportedFileType[];
};

export type FileExceedsMaxSizeError = {
  type: "file-exceeds-max-size";
  filename: string;
  maxFileSize: number;
};

export type FileExceedsMaxDimensionsError = {
  type: "file-exceeds-max-dimensions";
  filename: string;
  maxDimensions: Dimensions;
};

export type FileUploadValidationError =
  | InvalidFileError
  | UnsupportedFilesError
  | FileExceedsMaxDimensionsError
  | FileExceedsMaxSizeError;

function invalidFileError(file: File, bytes: number[]): InvalidFileError {
  return { type: "invalid-file", filename: file.name, bytes };
}

function unsupportedFilesError(
  files: File[],
  supportedFileTypes: SupportedFileType[],
): UnsupportedFilesError {
  return { type: "unsupported-files", filenames: files.map((f) => f.name), supportedFileTypes };
}

function fileExceedsMaxSizeError(file: File, maxFileSize: number): FileExceedsMaxSizeError {
  return { type: "file-exceeds-max-size", filename: file.name, maxFileSize };
}

function fileExceedingMaxDimensionsError(
  file: File,
  maxDimensions: Dimensions,
): FileExceedsMaxDimensionsError {
  return { type: "file-exceeds-max-dimensions", filename: file.name, maxDimensions };
}

export function isInvalidFileError(err: unknown): err is InvalidFileError {
  return get(err, "type") === "invalid-file";
}

export function isUnsupportedFilesError(err: unknown): err is UnsupportedFilesError {
  return get(err, "type") === "unsupported-files";
}

export function isFileExceedsMaxSizeError(err: unknown): err is FileExceedsMaxSizeError {
  return get(err, "type") === "file-exceeds-max-size";
}

export function isFileExceedsMaxDimensionsError(
  err: unknown,
): err is FileExceedsMaxDimensionsError {
  return get(err, "type") === "file-exceeds-max-dimensions";
}

export function isFileUploadValidationError(err: unknown): err is FileUploadValidationError {
  return (
    isInvalidFileError(err) ||
    isUnsupportedFilesError(err) ||
    isFileExceedsMaxSizeError(err) ||
    isFileExceedsMaxDimensionsError(err)
  );
}

export function getSupportedExtensions(
  supportedFileTypes: SupportedFileType[],
  params: { leadingDot?: boolean } = {},
): string[] {
  return supportedFileTypes
    .flatMap((s) => s.extensions)
    .map((ext) => (params.leadingDot ? `.${ext}` : ext));
}

export function supportedFileTypeFromFilename(
  filename: string,
  supportedFileTypes: SupportedFileType[],
): SupportedFileType | undefined {
  return when(getExtension(filename), (ext) =>
    supportedFileTypes.find((s) => s.extensions.includes(ext)),
  );
}

export function getUnsupportedFiles(
  files: File[],
  supportedFileTypes: SupportedFileType[],
): File[] {
  return files.filter((f) => !supportedFileTypeFromFilename(f.name, supportedFileTypes));
}

export function testMagicBytes(file: File, supportedFileType: SupportedFileType): Promise<File> {
  const length = max(supportedFileType.magicBytes.map((b) => b.length));
  const blob = file.slice(0, length);
  return blob.arrayBuffer().then((buf) => {
    const bytes = Array.from(new Uint8Array(buf));
    if (
      !supportedFileType.magicBytes.length ||
      supportedFileType.magicBytes.some((mb) => isEqual(mb, bytes.slice(0, mb.length)))
    ) {
      return file;
    } else {
      return Promise.reject(invalidFileError(file, Array.from(bytes)));
    }
  });
}

export function validateFiles(
  files: File[],
  { supportedFileTypes, maxFileSize, maxDimensions }: FileUploadConfig,
): Promise<File[]> {
  const unsupportedFiles = getUnsupportedFiles(files, supportedFileTypes);
  if (unsupportedFiles.length) {
    return Promise.reject(unsupportedFilesError(unsupportedFiles, supportedFileTypes));
  }

  const fileExceedingMaxFileSize = files.find((f) => f.size > maxFileSize);
  if (fileExceedingMaxFileSize) {
    return Promise.reject(fileExceedsMaxSizeError(fileExceedingMaxFileSize, maxFileSize));
  }

  return Promise.all(
    files.map((f) =>
      testMagicBytes(f, supportedFileTypeFromFilename(f.name, supportedFileTypes)!)
        .then((file) =>
          checkImageDimensions(file, maxDimensions)
            .then(() => file)
            .catch((dimensionError) => Promise.reject(dimensionError)),
        )
        .catch((fileTypeError) => Promise.reject(fileTypeError)),
    ),
  );
}

function checkImageDimensions(file: File, maxDimensions: Dimensions | undefined): Promise<File> {
  if (!maxDimensions) {
    return Promise.resolve(file);
  }
  const maxWidth = maxDimensions.width;
  const maxHeight = maxDimensions.height;
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectURL = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      if (width <= maxWidth && height <= maxHeight) {
        resolve(file);
      } else {
        reject(fileExceedingMaxDimensionsError(file, maxDimensions));
      }
    };

    img.onerror = () => {
      reject("Invalid image file");
    };

    img.src = objectURL;
  });
}

export type ProjectDecisionAttachment = {
  id: string;
  filename: string;
  selectedDocuments: DocumentType[];
};

export type UploadedAttachment = {
  id: string;
  filename: string;
  downloadUrl?: string;
};

export function toFormData(files: File[]) {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  return formData;
}

export function uploadAttachments(
  http: AxiosInstance,
  apiUrl: string,
  config: FileUploadConfig,
  files: File[],
): Promise<UploadedAttachment[]> {
  return validateFiles(files, config)
    .catch((err) => {
      logError(err, "Invalid attachments");
      return Promise.reject(err);
    })
    .then((validatedFiles) => http.post(apiUrl, toFormData(validatedFiles)))
    .then((res) => res.data)
    .catch((err) => {
      return Promise.reject(err);
    });
}
